import { Box, BoxProps, Flex, Heading, Text } from '@chakra-ui/react';
import { useTranslation } from 'next-i18next';

import GetTicket from '@components/event_detail/GetTicket';
import { EventType } from '@lib/types';

import useEventStatus from './useEventStatus';

type Props = {
  event: EventType;
} & BoxProps;

const renderPriceWithCurrency = ({
  price,
  currency,
}: {
  price: string | number;
  currency: string;
}) => {
  const formattedPrice = Number(price).toLocaleString('en-US');

  if (currency === 'NTD') {
    return `NT$${formattedPrice}`;
  }

  return `US$${formattedPrice}`;
};

const Ticket = ({ event, ...boxProps }: Props) => {
  const { t } = useTranslation(['common']);
  const { eventStatus, isEventOngoing } = useEventStatus({
    event,
  });

  const ticketingDisabled =
    eventStatus === 'past' ||
    (isEventOngoing && event.ticketingStatus === 'inactive-after-started') ||
    event.ticketingStatus === 'inactive';

  return (
    <Flex w="full" flexDirection="column" {...boxProps}>
      {event.tickets.map((ticket, idx) => {
        return (
          <Box
            bg="#fff"
            boxShadow={
              idx === 0
                ? '0px 0px 25px 0px rgba(0, 0, 0, 0.1)'
                : '0px 5px 10px 0px rgba(0, 0, 0, 0.1)'
            }
            p="30px"
            borderRadius="10px"
            borderBottom="1px dashed rgba(196, 196, 196, 0.9)"
            key={ticket.id}
          >
            <Heading fontWeight="400" fontSize="18px" lineHeight="34px">
              {ticket.name}
            </Heading>
            <Text fontWeight="700" fontSize="30px" lineHeight="34px">
              {renderPriceWithCurrency({
                price: ticket.price,
                currency: ticket.currency,
              })}
            </Text>

            {ticket.groupAttendeeNumber > 0 && ticket.groupPrice > 0 && (
              <Flex alignItems="flex-end" mt="1">
                <Flex>
                  <Text fontWeight="700" fontSize="30px" lineHeight="34px">
                    {renderPriceWithCurrency({
                      price: ticket.groupPrice,
                      currency: ticket.currency,
                    })}
                  </Text>
                </Flex>
                <Text fontSize="15px" lineHeight="31px" ml="1">
                  {t('event:Group ticket suffix', {
                    groupAttendeeNumber: ticket.groupAttendeeNumber,
                  })}
                </Text>
              </Flex>
            )}
          </Box>
        );
      })}

      <Box
        bg="#fff"
        boxShadow="0px 5px 10px 0px rgba(0, 0, 0, 0.1)"
        p="30px"
        borderRadius="10px"
      >
        <GetTicket event={event} disabled={ticketingDisabled} />
      </Box>
    </Flex>
  );
};

export default Ticket;
